// Not ported (dead in the Angular app): getFilingResponses
import { api, apiRequest } from '@/core/http/apiClient';
import { newIdempotencyKey } from '@/core/http/helpers';
import type {
  CreateTaxHandoffPayload,
  FilingAttemptHistoryRow,
  FilingHistoryResult,
  FilingPackageRow,
  FilingPreFlightReport,
  GenerateIxbrlPayload,
  IxbrlInspectorResult,
  IxbrlJobRow,
  IxbrlTagExceptionRow,
  PeriodOption,
  ResolveTagExceptionPayload,
  TaxHandoffPreview,
  TaxHandoffRow,
  TaxonomyConceptOption,
  TaxonomyVersionRow,
} from '@/core/models/phase4.models';

const ixbrlApi = 'Ixbrl/';
const filingApi = 'Filing/';
const taxApi = 'TaxHandoff/';

export const phase4Service = {
  // ---- Pickers and taxonomy ----

  getPeriodOptions: (clientId: string) => api.get<PeriodOption[]>(`${ixbrlApi}getPeriodOptions/${clientId}`),

  getTaxonomyVersions: (regulator?: string) =>
    api.get<TaxonomyVersionRow[]>(`${ixbrlApi}getTaxonomyVersions`, regulator ? { params: { regulator } } : {}),

  pinTaxonomy: (accountingPeriodId: string, documentVersionId: string, taxonomyVersionId: string) =>
    api.post<boolean>(`${ixbrlApi}pinTaxonomy/${accountingPeriodId}`, { documentVersionId, taxonomyVersionId }),

  // ---- iXBRL ----

  getJobs: (accountingPeriodId: string, documentVersionId?: string) =>
    api.get<IxbrlJobRow[]>(
      `${ixbrlApi}getJobs/${accountingPeriodId}`,
      documentVersionId ? { params: { documentVersionId } } : {},
    ),

  generate: (payload: GenerateIxbrlPayload) => api.post<IxbrlJobRow>(`${ixbrlApi}generate`, payload),

  getFacts: (accountingPeriodId: string, documentVersionId: string, search: string | null = null) =>
    api.get<IxbrlInspectorResult>(
      `${ixbrlApi}getFacts/${accountingPeriodId}/${documentVersionId}`,
      search ? { params: { search } } : {},
    ),

  getExceptions: (
    accountingPeriodId: string,
    documentVersionId: string | null = null,
    status: number | null = null,
  ) => {
    const params: Record<string, string> = {};
    if (documentVersionId) {
      params['documentVersionId'] = documentVersionId;
    }
    if (status !== null) {
      params['status'] = String(status);
    }
    return api.get<IxbrlTagExceptionRow[]>(`${ixbrlApi}getExceptions/${accountingPeriodId}`, { params });
  },

  getConcepts: (accountingPeriodId: string, documentVersionId: string, search: string | null = null) =>
    api.get<TaxonomyConceptOption[]>(
      `${ixbrlApi}getConcepts/${accountingPeriodId}/${documentVersionId}`,
      search ? { params: { search } } : {},
    ),

  resolveException: (accountingPeriodId: string, payload: ResolveTagExceptionPayload) =>
    api.post<IxbrlTagExceptionRow>(`${ixbrlApi}resolveException/${accountingPeriodId}`, payload),

  downloadReviewPack: (accountingPeriodId: string, documentVersionId: string, taxonomyVersionId?: string | null) =>
    apiRequest<Blob>('GET', `${ixbrlApi}downloadReviewPack/${accountingPeriodId}/${documentVersionId}`, {
      ...(taxonomyVersionId ? { params: { taxonomyVersionId } } : {}),
      responseType: 'blob',
    }),

  downloadIxbrl: (accountingPeriodId: string, documentVersionId: string, taxonomyVersionId?: string | null) =>
    apiRequest<Blob>('GET', `${ixbrlApi}downloadIxbrl/${accountingPeriodId}/${documentVersionId}`, {
      ...(taxonomyVersionId ? { params: { taxonomyVersionId } } : {}),
      responseType: 'blob',
    }),

  // ---- Filing ----

  preFlight: (accountingPeriodId: string, documentVersionId: string) =>
    api.post<FilingPreFlightReport>(`${filingApi}preFlight/${accountingPeriodId}/${documentVersionId}`, {}),

  preparePackage: (accountingPeriodId: string, documentVersionId: string) =>
    api.post<FilingPackageRow>(`${filingApi}preparePackage`, { accountingPeriodId, documentVersionId }),

  getFilingHistory: (accountingPeriodId: string) =>
    api.get<FilingHistoryResult>(`${filingApi}getHistory/${accountingPeriodId}`),

  refreshFilingStatus: (accountingPeriodId: string, filingAttemptId: string) =>
    api.post<FilingAttemptHistoryRow>(`${filingApi}refreshStatus/${accountingPeriodId}/${filingAttemptId}`, {}),

  // ---- Corporation Tax handoff ----

  previewHandoff: (accountingPeriodId: string, documentVersionId: string) =>
    api.get<TaxHandoffPreview>(`${taxApi}preview/${accountingPeriodId}/${documentVersionId}`),

  getHandoffs: (accountingPeriodId: string) => api.get<TaxHandoffRow[]>(`${taxApi}getHandoffs/${accountingPeriodId}`),

  handOff: (payload: CreateTaxHandoffPayload) =>
    api.post<TaxHandoffRow>(`${taxApi}handOff`, payload, { headers: { 'Idempotency-Key': newIdempotencyKey() } }),
};
